import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Search } from 'lucide-react';
import './MultiSelectDropdown.scss';

export default function MultiSelectDropdown({
  selectedItems,
  setSelectedItems,
  regionType = null,
  options = [],
  loading = false,
  onSearchChange = null,
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [apsLookup, setApsLookup] = useState({}); // Map aps_code -> aps_name for APS region type

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastSearchedTermRef = useRef(''); // Track last API call to prevent duplicates

  // Determine if using dynamic options or static zones
  const isDynamic = useMemo(() =>
    regionType !== null && onSearchChange !== null,
    [regionType, onSearchChange]
  );

  const staticZones = useMemo(() => [
    { id: 'nz', label: 'North Zone (NZ)' },
    { id: 'sz', label: 'South Zone (SZ)' },
    { id: 'ez', label: 'East Zone (EZ)' },
    { id: 'wz', label: 'West Zone (WZ)' },
    { id: 'cz', label: 'Central Zone (CZ)' }
  ], []);

  // Helper function to parse region options based on type
  const parseRegionOptions = useCallback((options, regionType) => {
    if (!Array.isArray(options)) return [];

    if (regionType === 'state') {
      return options.map(opt => ({
        id: opt.state__state,
        label: opt.state__state
      }));
    } else if (regionType === 'pincode') {
      return options.map(opt => ({
        id: opt.pincode,
        label: opt.pincode
      }));
    } else if (regionType === 'zone') {
      // Handle possible variations in API response
      return options.map(opt => ({
        id: opt.zone || opt.zone__zone,
        label: opt.zone || opt.zone__zone
      }));
    } else if (regionType === 'aps') {
      // Use compound key to handle duplicate aps_codes
      return options.map((opt, index) => ({
        id: `${opt.aps_code}|||${opt.aps_name}`, // Compound key with delimiter
        label: opt.aps_name,
        apsCode: opt.aps_code, // Store code separately for API submission
        apsName: opt.aps_name
      }));
    }
    return [];
  }, []);

  // Use dynamic options if provided, otherwise use static zones
  const dataSource = useMemo(() => {
    if (isDynamic) {
      const parsed = parseRegionOptions(options, regionType);
      return parsed;
    }
    return staticZones;
  }, [isDynamic, options, regionType, staticZones, parseRegionOptions]);

  // Accumulate APS lookup entries across multiple searches
  useEffect(() => {
    if (regionType === 'aps' && isDynamic && dataSource.length > 0) {
      setApsLookup(prevLookup => {
        const newLookup = { ...prevLookup }; // Preserve existing entries

        // Add/update entries from current search results
        dataSource.forEach(item => {
          if (item.apsCode && item.apsName) {
            newLookup[item.id] = {
              code: item.apsCode,
              name: item.apsName
            };
          }
        });

        return newLookup;
      });
    }
  }, [dataSource, regionType, isDynamic]);

  // For static zones, filter locally; for dynamic, options come from API
  const filteredItems = useMemo(() =>
    isDynamic
      ? dataSource
      : dataSource.filter(item =>
          item.label.toLowerCase().includes(searchTerm.toLowerCase())
        ),
    [isDynamic, dataSource, searchTerm]
  );
  const allSelected = useMemo(() => {
    if (dataSource.length === 0) return false;
    const selectedSet = new Set(selectedItems);
    return dataSource.every(z => selectedSet.has(z.id));
  }, [dataSource, selectedItems]);

  const toggleSelectAll = useCallback(() => {
    const currentIds = new Set(dataSource.map(z => z.id));
    setSelectedItems(prev => {
      const prevSet = new Set(prev);
      const allCurrentSelected = dataSource.every(z => prevSet.has(z.id));
      if (allCurrentSelected) {
        return prev.filter(id => !currentIds.has(id));
      } else {
        const nextSet = new Set(prev);
        dataSource.forEach(z => nextSet.add(z.id));
        return Array.from(nextSet);
      }
    });
  }, [dataSource, setSelectedItems]);

  // Debounce search term - only update debouncedSearchTerm after user stops typing for 500ms
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay - user must stop typing for half a second

    // Cleanup on unmount or when searchTerm changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  // Trigger API call only when debounced search term changes and meets criteria
  useEffect(() => {
    if (!isDynamic || !onSearchChange) return;

    // Skip if this is the same term we already searched for
    if (lastSearchedTermRef.current === debouncedSearchTerm) {
      return;
    }

    // Call API only if search term is >= 3 characters or empty (to clear)
    if (debouncedSearchTerm.length >= 3) {
      lastSearchedTermRef.current = debouncedSearchTerm;
      onSearchChange(debouncedSearchTerm);
    } else if (debouncedSearchTerm.length === 0) {
      lastSearchedTermRef.current = '';
      onSearchChange('');
    }
  }, [debouncedSearchTerm, isDynamic]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear search and APS lookup when region type changes
  useEffect(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    lastSearchedTermRef.current = ''; // Reset last searched term
    setApsLookup({}); // Clear APS lookup map
  }, [regionType]);

  // Memoized callbacks to prevent unnecessary re-renders
  // const toggleSelectAll = useCallback(() => {
  //   if (allSelected) {
  //     setSelectedItems([]);
  //   } else {
  //     setSelectedItems(dataSource.map(z => z.id));
  //   }
  // }, [allSelected, dataSource, setSelectedItems]);

  const toggleItem = useCallback((id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  }, [setSelectedItems]);

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  }, [disabled]);

  const getPlaceholder = useMemo(() => {
    if (isDynamic && searchTerm.length > 0 && searchTerm.length < 3) {
      return 'Type at least 3 characters to search';
    }
    return 'Search';
  }, [isDynamic, searchTerm.length]);

  const getItemsLabel = useMemo(() => {
    if (isDynamic) {
      const typeLabel = {
        zone: 'zone',
        state: 'state',
        aps: 'APS',
        pincode: 'pincode'
      }[regionType] || 'item';
      return typeLabel;
    }
    return 'zone';
  }, [isDynamic, regionType]);

  return (
    <div className="msd-page">
      <div className="msd">
        <div className="msd-group">
          <div className="msd-dropdown" ref={dropdownRef}>
            <div className={`msd-control${isOpen ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}>
              <Search className="msd-icon" />
              <input
                ref={inputRef}
                type="text"
                placeholder={getPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleInputClick}
                className="msd-input"
                disabled={disabled}
              />
            </div>

            {isOpen && (
              <div className="msd-menu" role="listbox" aria-label="Options">
                <div className="msd-list">
                  {isDynamic && searchTerm.length > 0 && searchTerm.length < 3 && (
                    <div className="msd-empty" style={{ color: '#666' }}>
                      Type at least 3 characters to search
                    </div>
                  )}

                  {loading && (
                    <div className="msd-empty" style={{ color: '#666' }}>
                      Loading...
                    </div>
                  )}

                  {!loading && isDynamic && searchTerm.length >= 3 && filteredItems.length === 0 && (
                    <div className="msd-empty">No results found</div>
                  )}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      <div className="msd-selectall" onClick={toggleSelectAll} role="option" aria-selected={allSelected}>
                        <div className="msd-radio">
                          <div className="circle" />
                          {allSelected && <div className="msd-radio-dot" />}
                        </div>
                        <span className="text">Select All</span>
                      </div>

                      {filteredItems.map((item) => {
                        const checked = selectedItems.includes(item.id);
                        return (
                          <div
                            key={item.id}
                            className="msd-option"
                            onClick={() => toggleItem(item.id)}
                            role="option"
                            aria-selected={checked}
                          >
                            <div className="msd-radio">
                              <div className="circle" />
                              {checked && <div className="msd-radio-dot" />}
                            </div>
                            <span className="text">{item.label}</span>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {!isDynamic && filteredItems.length === 0 && (
                    <div className="msd-empty">No results found</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {selectedItems.length > 0 && (
            <p className="msd-helper">
              {allSelected
                ? `All ${getItemsLabel}s selected`
                : `${selectedItems.length} ${getItemsLabel}${selectedItems.length > 1 ? 's' : ''} selected`}
            </p>
          )}
        </div>

        {selectedItems.length > 0 && (
          <div className="msd-selected">
            <p className="title">Selected {getItemsLabel.charAt(0).toUpperCase() + getItemsLabel.slice(1)}:</p>
            <div className="selected-list">
              {selectedItems.map((id) => {
                // For APS, display the name from lookup; for others, display the ID/value
                const displayValue = regionType === 'aps'
                  ? (apsLookup[id]?.name || id)
                  : id;

                return (
                  <div key={id} className="selected-item">
                    <span className="dot" />
                    {displayValue}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}